import {Button, Modal, Switch} from 'antd'
import classNames from 'classnames'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {renderIf, renderSelf} from 'views/Shared'
import {BsCaretRightFill} from "react-icons/bs";

const renderSpacingWrapper = (children, spacing = 'space-y-3 pl-2') =>
  spacing ? <div className={spacing ?? undefined}>{children}</div> : children

const RawAction = ({
                     label,
                     style,
                     onClick,
                     className,
                     background,
                     prefix = null,
                     suffix = null,
                     isLoading = false,
                     disabled = false,
                     color = 'text-color-300 hover:text-color-000',
                   }) => (
  <Translate>
    {(t) => (
      <div
        style={style}
        onClick={disabled || isLoading ? undefined : onClick}
        className={classNames(
          'flex items-center gap-1 font-medium text-sm capitalize cursor-pointer',
          color,
          background,
          className,
          disabled && 'disable'
        )}>
        {prefix}
        {_.isString(label) ? t(label) : label}
        {suffix}
      </div>
    )}
  </Translate>
)

export const DefaultActionButton = ({
                                      style,
                                      onClick,
                                      disabled,
                                      className,
                                      isLoading,
                                      label = null,
                                      prefix = null,
                                      suffix = null,
                                      type = 'primary',
                                      ...props
                                    }) => (
  <Button
    size="small"
    type={type}
    loading={isLoading}
    disabled={disabled || isLoading}
    onClick={disabled || isLoading ? undefined : onClick}
    style={
      style && {
        ...style,
        opacity: disabled ? 0.5 : 1,
      }
    }
    className={classNames(
      'px-3 flex gap-1 items-center capitalize rounded-md no-shadow no-text-shadow no-border text-xs leading-tight font-medium',
      className
    )}
    {...props}>
    {prefix}
    {_.isString(label) ? <Translate>{(t) => t(label)}</Translate> : label}
    {suffix}
  </Button>
)

export const ActionGroup = ({
                              style,
                              title,
                              spacing,
                              children,
                              className,
                              description,
                              border = false,
                            }) => (
  <Translate>
    {(t) => (
      <div
        style={style}
        className={classNames(
          'flex flex-col gap-2',
          border && 'border rounded',
          className
        )}>
        <div className="flex flex-col capitalize mb-1 italic">
          <div className="flex items-center gap-3">
            <BsCaretRightFill size={12} style={{marginBottom : '3px'}}/>
            <span className="font-bold text-sm text-color-000 uppercase">
              {t(title)}
            </span>
            <div className="flex-1 border-b"/>
          </div>
          {description && (
            <span className="text-doc-2xs text-color-400 leading-tight lowercase">
              {`( ${t(description)} )`}
            </span>
          )}
        </div>
        {renderSpacingWrapper(children, spacing)}
      </div>
    )}
  </Translate>
)

export const SimpleActionWrapper = ({
                                      style,
                                      children,
                                      className,
                                      title,
                                      description,
                                    }) => (
  <Translate>
    {(t) => (
      <div
        style={style}
        className={classNames('flex flex-col gap-1', className)}>
        <div className="flex items-center justify-between gap-2 rounded-md background-200 p-2">
          <span className="flex-1 font-bold text-xs text-color-000 italic">
            {t(title)}
          </span>
          {children}
        </div>
        {description && (
          <div className="p-1 text-xs text-color-400 italic leading-tight">
            {t(description)}
          </div>
        )}
      </div>
    )}
  </Translate>
)

export const SimpleActionButton = ({
                                     icon,
                                     color,
                                     title,
                                     label,
                                     style,
                                     prefix,
                                     suffix,
                                     onClick,
                                     disabled,
                                     className,
                                     background,
                                     description,
                                     Component = RawAction,
                                     withWrapper = () => ({}),
                                   }) => (
  <SimpleActionWrapper
    title={title}
    description={description}
    {...(withWrapper() ?? {})}>
    <Component
      {...{
        style,
        icon,
        color,
        label,
        prefix,
        suffix,
        onClick,
        disabled,
        className,
        background,
      }}
    />
  </SimpleActionWrapper>
)

export const ActionContainerWrapper = ({
                                         label,
                                         children,
                                         header = null,
                                         footer = null,
                                         renderLabel = (child, label) => child,
                                       }) => (
  <Translate>
    {(t) => (
      <div className="flex flex-col gap-1">
        {header}
        <div className="flex items-center justify-between gap-2 rounded-md background-200 p-2">
          {renderLabel(
            <span className="font-bold text-xs text-color-000 italic">
              {_.isString(label) ? t(label) : label}
            </span>,
            label
          )}
          {children}
        </div>
        {footer}
      </div>
    )}
  </Translate>
)

export const SwitchActionButton = ({
                                     name,
                                     label,
                                     checked,
                                     description,
                                     defaultChecked,
                                     isLoading = false,
                                     withWrapper = () => ({}),
                                     onClick = (value, event) => {
                                     },
                                     onChange = (value, event) => {
                                     },
                                     ...props
                                   }) => {
  const t = useTranslate()
  const subtitle = useMemo(
    () =>
      renderIf(
        description,
        <span className="p-1 text-xs text-color-400 italic leading-tight">
          {t(description)}
        </span>
      ),
    [description]
  )
  return (
    <ActionContainerWrapper
      label={label}
      renderLabel={(child, label) => (label ? child : subtitle)}
      footer={renderIf(label, subtitle)}
      {...(withWrapper() ?? {})}>
      <Switch
        name={name}
        checked={checked}
        onChange={onChange}
        onClick={onClick}
        loading={isLoading}
        defaultChecked={defaultChecked}
        {...props}
      />
    </ActionContainerWrapper>
  )
}

export const StatusActionWrapper = ({
                                      title,
                                      children,
                                      description,
                                      header = null,
                                      renderHeader = (child) => child,
                                    }) => (
  <Translate>
    {(t) => (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 px-2 py-1 background-200 rounded-md">
          <div className="flex flex-col flex-1">
            <span className="font-bold text-xs text-color-000 italic">
              {_.isString(title) ? t(title) : title}
            </span>
            {_.isFunction(renderHeader) ? renderHeader(header) : header}
          </div>
          {children}
        </div>
        <span className="p-1 text-xs text-color-400 italic leading-tight">
          {_.isString(description) ? t(description) : description}
        </span>
      </div>
    )}
  </Translate>
)

const StatusHeader = ({style, className, status, title = 'status'}) => (
  <Translate>
    {(t) => (
      <React.Fragment>
        <span className="text-2xs text-color-400 italic">{`${t(
          title
        )}: `}</span>
        <span
          style={style}
          className={classNames(
            'font-semibold italic text-xs leading-tight',
            className
          )}>
          {t(status)}
        </span>
      </React.Fragment>
    )}
  </Translate>
)

const defaultOption = {
  label: undefined,
  value: undefined,
  description: undefined,
  buttonProps: {
    label: undefined,
    style: undefined,
    color: undefined,
    className: undefined,
    background: undefined,
  },
}

export const StatusActionButton = ({
                                     title,
                                     status,
                                     prefix,
                                     suffix,
                                     onClick,
                                     options,
                                     disabled,
                                     isLoading,
                                     description,
                                     /**
                                      * 'style' is overrided by options => buttonProps
                                      */
                                     style,
                                     /**
                                      * 'style' is overrided by options => buttonProps
                                      */
                                     color,
                                     /**
                                      * 'color' is overrided by options => buttonProps
                                      */
                                     label,
                                     /**
                                      * 'label' is overrided by options => buttonProps
                                      */
                                     className,
                                     /**
                                      * 'className' is overrided by options => buttonProps
                                      */
                                     background,
                                     /**
                                      * 'background' is overrided by options => buttonProps
                                      */
                                     Component = RawAction,
                                     renderStatus = (child, status) => child,
                                     ...props
                                   }) => {
  const _current = _.find(options, {value: status})
  const params = _current?.buttonProps ?? {
    label,
    style,
    color,
    className,
    background,
  }
  return (
    <StatusActionWrapper
      title={title}
      description={description}
      renderHeader={(header) => renderStatus(header, _current)}
      header={
        <div className="flex items-baseline gap-1 flex-1">
          <StatusHeader
            title="status"
            style={_current?.style}
            status={_current?.label}
            className={_current?.className}
          />
        </div>
      }>
      <Component
        {...{
          style,
          prefix,
          suffix,
          onClick,
          disabled,
          isLoading,
          className,
          ...params,
          ...props,
        }}
      />
    </StatusActionWrapper>
  )
}

export const confirmActionDialog = ({
                                      title,
                                      content,
                                      onOk,
                                      onCancel,
                                      okText = 'yes',
                                      cancelText = 'no',
                                      translate = renderSelf,
                                      ...props
                                    }) =>
  Modal.confirm({
    onOk,
    onCancel,
    icon: null,
    okType: 'primary',
    className: 'custom-modal',
    okText: translate(okText),
    cancelText: translate(cancelText),
    okButtonProps: {
      className: 'rounded-lg no-shadow no-text-shadow no-border',
    },
    cancelButtonProps: {
      className: 'rounded-lg no-shadow no-text-shadow no-border text-color-200',
    },
    title: (
      <span className="font-bold text-lg text-color-000">
        {typeof title === "string" ? translate(title) : title}
      </span>
    ),
    content: (
      <span className="text-sm text-color-300 italic">
        {typeof content === "string" ? translate(content) : content}
      </span>
    ),
    ...props,
  })

// StatusActionButton.propTypes = {
//   status: PropTypes.any,
//   label: PropTypes.node,
//   title: PropTypes.node,
//   style: PropTypes.object,
//   color: PropTypes.string,
//   disabled: PropTypes.bool,
//   description: PropTypes.node,
//   className: PropTypes.string,
//   background: PropTypes.string,
//   options: PropTypes.arrayOf(
//     PropTypes.shape({
//       label: PropTypes.node,
//       value: PropTypes.any,
//       description: PropTypes.node,
//       buttonProps: PropTypes.shape({
//         style: PropTypes.object,
//         color: PropTypes.string,
//         className: PropTypes.string,
//         background: PropTypes.string,
//         label: PropTypes.node,
//       }),
//     })
//   ),
// }
